import React, {useCallback, useEffect, useRef, useState} from 'react';
import moment from 'moment';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, FormControl, InputLabel, InputAdornment,
  Select, IconButton, Alert, Tooltip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {isAutogeneratedRow} from '@mui/x-data-grid-premium';
import SampleDataGrid, {GRID_SAMPLE_DATE_COL_VAL, GRID_SAMPLE_LOCATION_COL_DEF} from '../../layouts/SampleDataGrid';
import {nullableDateComparator} from '../../utils/utils';
import {PUTAWAY_FORM_MENUACTION_DEF} from '../../forms/PutAwayForm';
import axios from 'axios';
import {DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/History';

function StyledTextField({property, label, object, onChange,...props}) {
  return (
    <TextField
      margin="dense"
      label={label}
      fullWidth
      variant="standard"
      value={object?.[property] || ''}
      onChange={e => onChange?.({[property]: e.target.value})}
      {...props}
    />
  );
}

function StyledDateTimePicker({property, label, object, onChange, tabIndex, ...props}) {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateTimePicker
        slotProps={{
          textField: {
            margin: "dense",
            fullWidth: true,
            variant: "standard",
            inputProps: {tabIndex},
          }
        }}
        format="M/D/YY h:mm A"
        label={label}
        value={object?.[property] || moment()}
        onChange={(newValue) => onChange?.({[property]: newValue})}
        {...props}
      />
    </LocalizationProvider>
  );
}

function StyledSelect({property, label, object, onChange, options, ...props}) {
  return (
    <FormControl fullWidth margin="dense" variant="standard">
      <InputLabel id={`${property}-label`}>{label}</InputLabel>
      <Select
        labelId={`${property}-label`}
        id={`${property}-select`}
        value={object?.[property] || ''}
        onChange={e => onChange?.({[property]: e.target.value})}
        fullWidth
        {...props}
      >
        {options.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function ChemicalUniqueIdField({value, onChange}) {
  const [warning, setWarning] = useState('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const generate = useCallback(() => axios.get('/api/wetlab/chemical/unique-id?count=1')
    .then(({data}) => onChangeRef.current(data.unique_ids[0])), []);
  useEffect(() => {
    if (!value) {
      setWarning('');
      return undefined;
    }
    const timeout = setTimeout(() => {
      axios.get(`/api/wetlab/chemical/unique-id/check?q=${encodeURIComponent(value)}`)
        .then(({data}) => setWarning(data.ok ? '' : 'Already taken'));
    }, 300);
    return () => clearTimeout(timeout);
  }, [value]);
  useEffect(() => {
    generate();
  }, [generate]);
  return (
    <TextField
      fullWidth
      margin="dense"
      variant="standard"
      label="Unique ID"
      value={value || ''}
      error={!!warning}
      helperText={warning}
      onChange={event => onChange(event.target.value.toUpperCase())}
      slotProps={{
        input: {
          endAdornment:
            <InputAdornment position="end">
              <IconButton
                aria-label="Generate new unique ID"
                onClick={generate}
                edge="end"
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </InputAdornment>,
        },
      }}
    />
  );
}

function AddChemicalForm({open, onClose, onSubmit}) {
  const [chemical, setChemical] = useState({
    date: moment(), physical_state: 'solid',
  });
  const [molecularWeightError, setMolecularWeightError] = useState('');
  const change = changes => setChemical(prev => ({...prev, ...changes}));
  useEffect(() => {
    if (!chemical.smiles?.trim()) {
      setMolecularWeightError('');
      setChemical(prev => ({...prev, molecular_weight: ''}));
      return undefined;
    }
    const timeout = setTimeout(() => {
      axios.get('/api/wetlab/chemical/molecular-weight', {params: {smiles: chemical.smiles}})
        .then(({data}) => {
          setMolecularWeightError('');
          setChemical(prev => ({...prev, molecular_weight: data.molecular_weight}));
        })
        .catch(error => {
          setMolecularWeightError(error.response?.data?.error || {mw: 'Unable to calculate molecular weight'});
          setChemical(prev => ({...prev, molecular_weight: ''}));
        });
    }, 300);
    return () => clearTimeout(timeout);
  }, [chemical.smiles]);
  const handleSubmit = object => {
    if (object.physical_state === 'solid')
      onSubmit?.(Object.fromEntries(Object.entries(object).filter(([key]) => key !== 'amount_ul' && key !== 'concentration_value' && key !== 'concentration_unit')));
    else if (object.physical_state === 'solution')
      onSubmit?.(Object.fromEntries(Object.entries(object).filter(([key]) => key !== 'amount_mg')));
    else
      onSubmit?.(object);
  }
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={event => {event.preventDefault(); handleSubmit(chemical); onClose();}}>
        <DialogTitle>New Chemical</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item size={{xs: 8}}>
              <StyledTextField object={chemical} property="name" label="Name" onChange={change} autoFocus />
            </Grid>
            <Grid item size={{xs: 4}}>
              <ChemicalUniqueIdField value={chemical.unique_id} onChange={unique_id => change({unique_id})} />
            </Grid>
          </Grid>
          <StyledTextField object={chemical} property="smiles" label="SMILES" onChange={change} multiline
            error={!!molecularWeightError?.smiles}
            helperText={molecularWeightError?.smiles}
          />
          <StyledTextField object={chemical} property="molecular_weight" label="Molecular Weight" onChange={change}
            color={molecularWeightError?.mw ? 'warning' : 'inherit'}
            helperText={molecularWeightError?.mw}
            slotProps={{
              htmlInput: {inputMode: "numeric"},
              input: {endAdornment: <InputAdornment position="end">g/mol</InputAdornment>},
            }}
            sx={{input: {textAlign: "right"}}}
          />
          <StyledSelect object={chemical} property="physical_state" label="Physical State" onChange={change}
            options={[
              {value: 'solid', label: 'Solid'},
              {value: 'solution', label: 'Solution'}
            ]}
          />
          {chemical.physical_state === 'solution' && (
            <>
              <Grid container spacing={1}>
                <Grid item size={{ xs: 5 }}>
                  <StyledTextField object={chemical} property="amount_ul" label="Volume" onChange={change}
                    slotProps={{
                      htmlInput: {inputMode: "numeric"},
                      input: {endAdornment: <InputAdornment position="end">μL</InputAdornment>},
                    }}
                    sx={{input: {textAlign: "right"}}}
                  />
                </Grid>
                <Grid item size={{ xs: 4 }}>
                  <StyledTextField
                    object={chemical}
                    property="concentration_value"
                    label="Concentration"
                    onChange={change}
                    slotProps={{
                      htmlInput: {inputMode: "numeric"},
                    }}
                    sx={{input: {textAlign: "right"}}}
                  />
                </Grid>
                <Grid item size={{ xs: 3 }}>
                  <StyledSelect
                    object={chemical}
                    property="concentration_unit"
                    label="Unit"
                    onChange={change}
                    options={[
                      {value: 'mM', label: 'mM'},
                      {value: 'mg/mL', label: 'mg/mL'}
                    ]}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {chemical.physical_state === 'solid' && (
            <StyledTextField object={chemical} property="amount_mg" label="Amount" onChange={change}
              slotProps={{
                htmlInput: {inputMode: "numeric"},
                input: {endAdornment: <InputAdornment position="end">mg</InputAdornment>},
              }}
              sx={{input: {textAlign: "right"}}}
            />
          )}
          <StyledTextField object={chemical} property="cas_number" label="CAS Number" onChange={change} />
          <Grid container spacing={1}>
            <Grid item size={{xs: 6}}><StyledTextField object={chemical} property="vendor" label="Vendor" onChange={change} /></Grid>
            <Grid item size={{xs: 6}}><StyledTextField object={chemical} property="vendor_catalog_number" label="Catalog Number" onChange={change} /></Grid>
          </Grid>
          <StyledDateTimePicker object={chemical} property="date" label="Date" onChange={change} />
          <StyledTextField object={chemical} property="description" label="Description" onChange={change} multiline />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!chemical?.name || (chemical?.physical_state === "solid" ? !chemical?.amount_mg : !chemical?.amount_ul || !chemical?.concentration_value || !chemical?.concentration_unit)}>Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ChemicalHistoryForm({open, onClose, row}) {
  const history = row?.history || [];
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>History of {row.unique_id} ({row.name})</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{minWidth: 180, maxWidth: 180}}>Date</TableCell>
              <TableCell sx={{minWidth: 120, maxWidth: 120}}>Event</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((event, index) => {
              const details = Object.entries(event)
                .filter(([key]) => key !== 'type' && key !== 'date')
                .map(([key, value]) => `${key.replaceAll('_', ' ')}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('; ');
              return (
                <TableRow key={`${event.date || 'event'}-${index}`}>
                  <TableCell>{event.date ? moment(event.date).format('M/D/YY h:mm A') : ''}</TableCell>
                  <TableCell>{event.type || ''}</TableCell>
                  <TableCell>{details}</TableCell>
                </TableRow>
              );
            })}
            {history.length === 0 && (
              <TableRow><TableCell colSpan={3}>No history recorded.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function DispenseForm({open, onClose, row: rows, setData, showMessage}) {
  const [values, setValues] = useState({date: moment(), entries: {}});
  const [availableAliquots, setAvailableAliquots] = useState({});
  const compounds = (rows || []).map(row => row.children?.[0].name)
  console.log(compounds)
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    const params = new URLSearchParams();
    compounds.forEach(compound => params.append('name', compound));
    axios.get(`/api/wetlab/chemical/available-aliquots?${params}`)
      .then(({data}) => {
        setAvailableAliquots(data)
        setValues(prev => ({
          ...prev,
          entries: Object.fromEntries(compounds.map(compound => [
            compound,
            {
              amount_ul: 1,
              ...prev.entries[compound],
              id: data[compound]?.[0]?.id || '',
            }
          ]))})
        );
      })
  }, [open]);
  const changeEntry = (name, changes) => setValues(prev => ({
    ...prev,
    entries: {...prev.entries, [name]: {...prev.entries[name], ...changes}},
  }));
  const submit = event => {
    event.preventDefault();
    const entries = Object.values(values.entries).map(entry => ({id: entry.id, amount_ul: Number(entry.amount_ul)}));
    axios.post('/api/wetlab/chemical/dispense', {entries, date: values.date?.toISOString()})
      .then(() => {
        setData?.(prev => prev.map(item => {
          const entry = values.entries[item.name];
          if (!entry || item.id !== entry.id) return item;
          return {...item, amount_ul: item.amount_ul - Number(entry.amount_ul)};
        }));
        showMessage?.('Successfully recorded dispense');
        onClose?.();
      })
      .catch(error => {
        setError(error.response?.data?.error || 'Error recording dispense');
      });
  };
  const valid = compounds.length > 0 && compounds.every(compound => {
    const entry = values.entries[compound];
    return entry?.id && Number(entry.amount_ul) > 0;
  });
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>Dispense Compound{compounds.length > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">Compound</TableCell>
                <TableCell>Aliquot</TableCell>
                <TableCell>Volume</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {compounds.map((compound, index) => (
                <TableRow key={compound}>
                  <TableCell align="right"><Typography>{compound}</Typography></TableCell>
                  <TableCell sx={{width: 200}}>
                    <Select
                      fullWidth
                      variant="standard"
                      property="id"
                      onChange={e => changeEntry(compound, {id: e.target.value})}
                      value={values.entries[compound]?.id || ''}
                      inputProps={{tabIndex: index + 1}}
                      sx={{width: 200}}
                    >
                      {availableAliquots[compound]?.map(aliquot => 
                        <MenuItem key={aliquot.id} value={aliquot.id}>
                          <Typography variant="body2" component="span" sx={{mr: 1}}>
                            {aliquot.unique_id}
                          </Typography>
                          <Typography variant="caption" component="span" sx={{mr: 1}} color="textSecondary">
                            {aliquot.concentration_value} {aliquot.concentration_unit} x {aliquot.amount_ul} μL
                          </Typography>
                        </MenuItem>
                      )}
                    </Select>
                  </TableCell>
                  <TableCell sx={{width: 120}}>
                    <StyledTextField
                      object={values.entries[compound]}
                      property="amount_ul"
                      onChange={changes => changeEntry(compound, changes)}
                      slotProps={{
                        htmlInput: {inputMode: "numeric", tabIndex: compounds.length + index + 1},
                        input: {endAdornment: <InputAdornment position="end">μL</InputAdornment>},
                      }}
                      sx={{input: {textAlign: "right"}, width: 120}}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <StyledDateTimePicker
            object={values}
            property="date"
            label="Date"
            tabIndex={compounds.length * 2 + 1}
            onChange={changes => setValues(prev => ({...prev, ...changes}))}
          />
          {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!valid}>Submit</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ChemicalEventForm({open, onClose, row, setData, showMessage}) {
  const [values, setValues] = useState(null);
  useEffect(() => {
    if (open && row) {
      setValues(prev => ({
        date: moment(),
        num_aliquots: 1,
        ul_per_aliquot: 10,
        ...prev,
        concentration_value: row.concentration_value,
        concentration_unit: row.concentration_unit,
      }));
      axios.get(`/api/wetlab/chemical/unique-id?count=1`)
        .then(({data}) => setValues(current => ({...current, unique_ids: data.unique_ids})));
    }
  }, [open]);
  const change = changes => {
    setValues(prev => ({...prev, ...changes}));
    if (changes.num_aliquots && changes.num_aliquots !== values?.num_aliquots) {
      axios.get(`/api/wetlab/chemical/unique-id?count=${Math.max(1, Number(changes.num_aliquots))}`)
        .then(({data}) => setValues(current => ({...current, unique_ids: data.unique_ids})));
    }
  };
  useEffect(() => {
    if (!open || !row || !values) return;
    const params = new URLSearchParams({
      id: row.id,
      num_aliquots: values.num_aliquots,
      ul_per_aliquot: values.ul_per_aliquot,
      concentration_value: values.concentration_value ?? '',
      concentration_unit: values.concentration_unit ?? '',
    });
    setValues(current => current?.instructions ? ({...current, instructions: null, severity: null}) : current);
    axios.get(`/api/wetlab/chemical/aliquot-instructions?${params}`)
      .then(({data}) => setValues(current => ({...current, ...data})));
  }, [open, values?.num_aliquots, values?.ul_per_aliquot, values?.concentration_value, values?.concentration_unit]);
  const printLabels = () => {
    const labels = (values?.unique_ids || []).map(uniqueId => [
      row.name,
      `${values.ul_per_aliquot}μL x ${values.concentration_value}${values.concentration_unit}`,
      `${uniqueId} ${moment(values.date).format('M/D/YY')}`,
    ].join('\t')).join('\n');
    navigator.clipboard?.writeText(labels);
    showMessage?.('Labels copied to clipboard');
  };
  const submit = async event => {
    event.preventDefault();
    const dummy_new_aliquots = Array.from({length: values.num_aliquots}, (_, i) => ({
      ...row,
      id: `${row.id}-aliquot-${Date.now()}-${i}`,
      unique_id: values.unique_ids[i],
      amount_ul: values.ul_per_aliquot,
      concentration_value: values.concentration_value,
      concentration_unit: values.concentration_unit,
      date: values.date.toISOString(),
    }));
    axios.post('/api/wetlab/chemical/aliquot', {id: row.id, ...values})
      .then(({data: {aliquot_ids}}) => {
        // Update the dummy new aliquots in the data grid with the real IDs returned from the server
        setData?.(prev => prev.map(item => {
          const index = dummy_new_aliquots.findIndex(dummy => dummy.id === item.id);
          if (index !== -1) {
            return {...item, id: aliquot_ids[index]};
          }
          return item;
        }));
        showMessage?.('Successfully created new aliquots')
      })
      .catch(error => {
        setValues(prev => ({...prev, instructions: error.response?.data?.error || 'Error creating aliquots', severity: 'error'}));
      });
    setData?.(prev => [...(row.physical_state === 'solid' ? prev.filter(item => item.id !== row.id) : prev), ...dummy_new_aliquots])
    onClose?.();
  };
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{row ? `Aliquot ${row.name}` : 'Aliquot'}</DialogTitle>
        <DialogContent>
          <StyledTextField object={values} property="num_aliquots" label="Number of Aliquots" onChange={change} autoFocus
            slotProps={{
              htmlInput: {inputMode: "numeric"},
            }}
            sx={{input: {textAlign: "right"}}}
          />
          <StyledTextField object={values} property="ul_per_aliquot" label="Volume per Aliquot" onChange={change}
            slotProps={{
              htmlInput: {inputMode: "numeric"},
              input: {endAdornment: <InputAdornment position="end">μL</InputAdornment>},
            }}
            sx={{input: {textAlign: "right"}}}
          />
          <Grid container spacing={1}>
            <Grid item size={{ xs: 8 }}>
              <StyledTextField object={values} property="concentration_value" label="Concentration" onChange={change}
                slotProps={{
                  htmlInput: {inputMode: "numeric"},
                }}
                sx={{input: {textAlign: "right"}}}
              />
            </Grid>
            <Grid item size={{ xs: 4 }}>
              <FormControl fullWidth margin="dense" variant="standard">
                <InputLabel id="concentration-unit-label">Unit</InputLabel>
                <Select
                  labelId="concentration-unit-label"
                  id="concentration-unit-select"
                  value={values?.concentration_unit || ''}
                  onChange={e => change({concentration_unit: e.target.value})}
                  fullWidth
                >
                  <MenuItem value='mM'>mM</MenuItem>
                  <MenuItem value='mg/mL'>mg/mL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <StyledDateTimePicker object={values} property="date" label="Date" onChange={change} />
          {values?.instructions && <Alert severity={values.severity} sx={{mt: 2}}>{values.instructions}</Alert>}
          <Typography variant="caption" color="textSecondary" sx={{mt: 2, mb: 1, display: 'block'}}>
            Unique IDs: {(values?.unique_ids || []).join(', ')}
          </Typography>
          <Button startIcon={<PrintIcon />} onClick={printLabels} disabled={!values?.unique_ids?.length} variant="outlined">
            Print Labels
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={values?.severity === "error"}>Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function Chemical() {
  return <SampleDataGrid
    api={{create: '/api/wetlab/chemical/add', read: '/api/wetlab/chemical/get', update: '/api/wetlab/chemical/update', delete: '/api/wetlab/chemical/discard'}}
    AddForm={AddChemicalForm}
    actions={[
      PUTAWAY_FORM_MENUACTION_DEF('/api/wetlab/chemical/update'),
      {
        label: 'Dispense',
        icon: <WorkspacesIcon />,
        Form: DispenseForm,
        multiple: 'only',
        shouldShow: params => isAutogeneratedRow(params.row),
        getRowIds: ({selectedIds, apiRef}) => selectedIds.flatMap(groupId =>
          apiRef.current.getRowGroupChildren({groupId, applyFiltering: false})
            .map(id => apiRef.current.getRow(id))
            .filter(row => row && !isAutogeneratedRow(row))
            .map(row => row.id)
        ),
      },
      {
        label: 'Aliquot',
        icon: <WorkspacesIcon />,
        Form: ChemicalEventForm,
      },
      {
        label: 'History',
        icon: <HistoryIcon />,
        Form: ChemicalHistoryForm,
        shouldShow: params => !isAutogeneratedRow(params.row),
      },
    ]}
    columns={[
      {field: 'unique_id', headerName: 'Unique ID', width: 110, editable: true},
      {
        field: 'name',
        headerName: 'Name',
        width: 140,
        editable: true,
        renderCell: params => (
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
            {params.value}
            <Tooltip
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'white',
                    padding: 1,
                    maxWidth: 'none',
                  },
                },
                arrow: {
                  sx: {
                    color: 'white',
                  },
                },
              }}
              title={
                <img
                  alt={`Chemical structure for ${params.value}`}
                  src={`/api/wetlab/chemical/structure?name=${encodeURIComponent(params.value)}`}
                  style={{
                    display: 'block',
                    width: 'auto',
                    height: 'auto',
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }}
                />
              }
            >
              <SearchIcon color="disabled" fontSize="small" />
            </Tooltip>
          </span>
        ),
      },
      {field: 'cas_number', headerName: 'CAS No.', width: 110, editable: true},
      {field: 'smiles', headerName: 'SMILES', width: 200, editable: true},
      {field: 'vendor', headerName: 'Vendor', width: 110, editable: true},
      {field: 'vendor_catalog_number', headerName: 'Catalog No.', width: 110, editable: true},
      {field: 'molecular_weight', headerName: 'Mol. Weight', width: 120, type: 'number', valueFormatter: value => value && `${value.toLocaleString(undefined, {maximumFractionDigits: 3})} g/mol`},
      {field: 'physical_state', headerName: 'State', width: 90},
      {field: 'amount_mg', headerName: 'mg', width: 80, type: 'number', editable: true},
      {field: 'amount_ul', headerName: 'μL', width: 80, type: 'number', editable: true},
      {
        field: 'amount',
        headerName: 'Amount',
        type: 'number',
        editable: false,
        width: 80,
        valueGetter: (_, row) => {
          if (row.physical_state === 'solid') return row.amount_mg
          else if (row.physical_state === 'solution') return row.amount_ul
          else return null
        },
        valueFormatter: (value, row) => {
          if (!value) return '';
          if (row.physical_state === 'solid') return `${value.toLocaleString(undefined, {maximumFractionDigits: 3})} mg`;
          else if (row.physical_state === 'solution') return `${value.toLocaleString(undefined, {maximumFractionDigits: 3})} μL`;
          else return '';
        }
      },
      {
        field: 'concentration_value',
        headerName: 'Concentration',
        type: 'number',
        width: 120,
        valueFormatter: (value, row) => value == null ? '' : `${value.toLocaleString(undefined, {maximumFractionDigits: 3})} ${row.concentration_unit}`
      },
      ...GRID_SAMPLE_LOCATION_COL_DEF(),
      {field: 'description', headerName: 'Description', flex: 1, editable: true},
      {field: 'date', type: 'date', headerName: 'Date', width: 110, editable: true, ...GRID_SAMPLE_DATE_COL_VAL('date'), sortComparator: nullableDateComparator},
    ]}
    getPrintLabel={chemical => [
      chemical.name,
      chemical.physical_state === 'solid' ? `${chemical.amount_mg}mg` : `${chemical.amount_ul}μL x ${chemical.concentration_value}${chemical.concentration_unit}`,
      `${chemical.unique_id} ${moment(chemical.date).format('M/D/YY')}`,
    ]}
    isRowSelectable={(params, selectedRows) => {
      if (selectedRows.length === 0)
        return true;
      const hasSelectedGroup = selectedRows.some(isAutogeneratedRow);
      return hasSelectedGroup === isAutogeneratedRow(params.row);
    }}
    rowSelectionPropagation={{descendants: false, parents: false}}
    groupingColDef={{hideDescendantCount: true}}
    getAggregationPosition={(groupNode) => groupNode.depth === -1 ? null : 'inline'}
    initialState={{
      rowGrouping: {model: ['name']},
      sorting: {sortModel: [{field: 'name', sort: 'asc'}]},
      columns: {columnVisibilityModel: {
        amount_mg: false,
        amount_ul: false,
        cas_number: false,
        smiles: false,
        vendor: false,
        vendor_catalog_number: false,
        molecular_weight: false,
        physical_state: false,
      }},
      aggregation: {model: {
        smiles: 'consensus',
        description: 'consensus',
        date: 'min',
      }},
    }}
  />;
}
